import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Database, Users, FileText, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-cyan-600 rounded-2xl mb-6 shadow-lg">
            <Shield className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600 italic">Last updated: March 3, 2026</p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <p className="text-gray-700 leading-relaxed">
            ScaleNix ("we", "our", "us") built the <strong>ScaleNix Zimbra Mail</strong> app.
            This Privacy Policy explains how we collect, use, and protect your information when
            you use our application.
          </p>
        </motion.div>

        {/* Section 1: Information We Collect */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
              <Database className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-indigo-600">1. Information We Collect</h2>
          </div>
          <p className="text-gray-700 mb-4">
            ScaleNix Zimbra Mail connects directly to your Zimbra server. We collect and store
            the following information <strong>locally on your device only</strong>:
          </p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                <strong>Account credentials</strong>: Your Zimbra server URL, email address,
                and password, stored in encrypted form using Android's EncryptedSharedPreferences.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                <strong>Email, calendar, contacts, and task data</strong>: Synced from your
                Zimbra server and cached locally in an on-device database for offline access.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                <strong>App preferences</strong>: Your notification and sync settings.
              </span>
            </li>
          </ul>
        </motion.div>

        {/* Section 2: How We Use Your Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-indigo-600">2. How We Use Your Information</h2>
          </div>
          <p className="text-gray-700 mb-4">Your information is used solely to:</p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Authenticate with your Zimbra server</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Display and manage your emails, calendar events, contacts, and tasks</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Send push notifications for new messages</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Synchronize data between your device and your Zimbra server</span>
            </li>
          </ul>
        </motion.div>

        {/* Section 3: Data Storage and Security */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-indigo-600">3. Data Storage and Security</h2>
          </div>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                All data is stored <strong>locally on your device</strong>. We do not operate
                any external servers.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                Your credentials are encrypted using Android's security library
                (EncryptedSharedPreferences backed by Android Keystore).
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                All network communication with your Zimbra server uses HTTPS encryption.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Cached data is stored in a local Room database on your device.</span>
            </li>
          </ul>
        </motion.div>

        {/* Section 4: Data Sharing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-indigo-50 to-cyan-50 rounded-xl shadow-lg p-8 mb-8 border-2 border-indigo-200"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
              <Users className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-indigo-600">4. Data Sharing</h2>
          </div>
          <p className="text-gray-900 font-semibold text-lg mb-2">
            We do not share, sell, or transmit your personal data to any third party.
          </p>
          <p className="text-gray-700">
            Your data is exchanged only between your device and the Zimbra server you configure.
            No data is sent to ScaleNix or any other external service.
          </p>
        </motion.div>

        {/* Section 5: Third-Party Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-indigo-600 mb-4">5. Third-Party Services</h2>
          <p className="text-gray-700">
            This app does not use any third-party analytics, advertising, or tracking services.
            There are no ads in this application.
          </p>
        </motion.div>

        {/* Section 6: Data Retention and Deletion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-indigo-600 mb-4">6. Data Retention and Deletion</h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Your data remains on your device as long as the app is installed.</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                You can remove an account from the app at any time, which deletes all locally
                stored data for that account.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                Uninstalling the app removes all locally stored data, including cached emails,
                credentials, and preferences.
              </span>
            </li>
          </ul>
        </motion.div>

        {/* Section 7: Children's Privacy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-indigo-600 mb-4">7. Children's Privacy</h2>
          <p className="text-gray-700">
            This app is not directed at children under the age of 13. We do not knowingly collect
            personal information from children under 13. This is a professional email client intended
            for use by individuals with a Zimbra email account.
          </p>
        </motion.div>

        {/* Section 8: Permissions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-indigo-600 mb-4">8. Permissions</h2>
          <p className="text-gray-700 mb-4">The app requests the following Android permissions:</p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                <strong>Internet</strong>: To connect to your Zimbra server.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                <strong>Notifications</strong>: To alert you about new emails and calendar events.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                <strong>Foreground Service</strong>: To maintain real-time push notification delivery.
              </span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>
                <strong>Boot Completed</strong>: To restart background sync after device reboot.
              </span>
            </li>
          </ul>
        </motion.div>

        {/* Section 9: Changes to This Policy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-indigo-600 mb-4">9. Changes to This Policy</h2>
          <p className="text-gray-700">
            We may update this Privacy Policy from time to time. Any changes will be posted on
            this page with an updated revision date.
          </p>
        </motion.div>

        {/* Section 10: Contact Us */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-indigo-600">10. Contact Us</h2>
          </div>
          <p className="text-gray-700 mb-4">
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <div className="bg-gradient-to-br from-indigo-50 to-cyan-50 rounded-lg p-4 border border-indigo-200">
            <p className="text-gray-900">
              <strong>Email</strong>: <a href="mailto:support@scalenix.fr" className="text-indigo-600 hover:text-indigo-700 underline">support@scalenix.fr</a>
            </p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="text-center py-8"
        >
          <p className="text-gray-600 text-sm">
            © 2026 ScaleNix. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
